import React, { useCallback, useEffect, useState } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Stack,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Menu,
  MenuItem,
  Chip,
  Badge,
  Alert,
} from "@mui/material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTenant } from "../contexts/TenantContext";
import { useCart } from "../contexts/CartContext";
import { registerForClass } from "../api/backend";
import GlobalAdminSwitcher from "./GlobalAdminSwitcher";
import { restoreUser, endImpersonationSession } from "../store/authSlice";
import apiClient, { clearImpersonationToken, setActiveTenant, fetchMessagingThreads } from "../api/apiClient";
import { endImpersonationAudit } from "../api/globalAdminApi";
import { setCurrentUser } from "../utils/auth";
import { getDevClock } from "../api/devClockApi";
import { GLOBAL_ADMIN_PATHS } from "../pages/globalAdmin/paths";

// Icons
import SchoolIcon from "@mui/icons-material/School";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessIcon from "@mui/icons-material/Business";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HistoryIcon from "@mui/icons-material/History";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import GridViewIcon from "@mui/icons-material/GridView";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ForumIcon from "@mui/icons-material/Forum";
import SettingsIcon from "@mui/icons-material/Settings";
import TenantLogo from "./TenantLogo";

const SIDEBAR_WIDTH = 240;
const TOP_NAV_HEIGHT = 64;
const VIEW_LABELS = { INSTRUCTOR: "Instructor", COURSE_COORDINATOR: "Coordinator", ADMIN: "Admin", STUDENT: "Student" };

// ── Design tokens (Sidebar Nav.html) ─────────────────────────────────────────
const SB = {
  bg: "var(--color-surface-press-light)",
  surface: "#FFFFFF",
  surface2: "#f7f7fa",
  surface3: "var(--color-surface-press-light)",
  line: "#e5e7eb",
  line2: "#efeff3",
  ink: "#1F1633",
  ink2: "#4A4257",
  ink3: "#7B7389",
  ink4: "#A6A0B3",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  shadowSm: "0 1px 0 rgba(21,15,35,.04), 0 1px 3px rgba(21,15,35,.08)",
  shadowMd: "0 8px 28px -10px rgba(21,15,35,.20)",
};

// ── Sidebar section label ────────────────────────────────────────────────────
function SidebarSectionLabel({ label }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: "8px",
      px: "10px", py: "12px 10px 6px",
      fontSize: "10px", fontWeight: 700, letterSpacing: ".1em",
      textTransform: "uppercase", color: SB.ink4,
    }}>
      {label}
      <Box sx={{ flex: 1, height: "1px", bgcolor: SB.line2 }} />
    </Box>
  );
}

// ── Sidebar nav item ─────────────────────────────────────────────────────────
function bestMatch(pathname, items) {
  let best = null;
  let bestLen = -1;
  for (const it of items) {
    const p = it.to.split("?")[0];
    const isMatch =
      p === "/"
        ? pathname === "/"
        : pathname === p || pathname.startsWith(`${p}/`);
    if (isMatch && p.length > bestLen) {
      best = it.to;
      bestLen = p.length;
    }
  }
  return best;
}

function TopNavMenu({ section, activeTo, primaryMain, primaryBg, icon: SectionIcon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = React.useRef(null);
  const openTimerRef = React.useRef(null);
  const closeTimerRef = React.useRef(null);
  const visibilityTimerRef = React.useRef(null);

  const clearTimer = (timerRef) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const openImmediately = () => {
    clearTimer(openTimerRef);
    clearTimer(closeTimerRef);
    clearTimer(visibilityTimerRef);
    setIsVisible(true);
    setIsOpen(true);
  };

  const scheduleOpen = () => {
    clearTimer(closeTimerRef);
    clearTimer(visibilityTimerRef);
    if (isOpen || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setIsVisible(true);
      setIsOpen(true);
    }, 100);
  };

  const scheduleClose = () => {
    clearTimer(openTimerRef);
    clearTimer(closeTimerRef);
    if (!isOpen) return;
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setIsOpen(false);
      clearTimer(visibilityTimerRef);
      visibilityTimerRef.current = setTimeout(() => {
        visibilityTimerRef.current = null;
        setIsVisible(false);
      }, 180);
    }, 250);
  };

  useEffect(() => () => {
    clearTimer(openTimerRef);
    clearTimer(closeTimerRef);
    clearTimer(visibilityTimerRef);
  }, []);

  // Choosing a link navigates but the pointer/focus is still on the menu, so it would stay open.
  const { pathname } = useLocation();
  const firstPathRef = React.useRef(pathname);
  useEffect(() => {
    if (firstPathRef.current === pathname) return;
    firstPathRef.current = pathname;
    clearTimer(openTimerRef);
    clearTimer(closeTimerRef);
    clearTimer(visibilityTimerRef);
    setIsOpen(false);
    setIsVisible(false);
    if (containerRef.current?.contains(document.activeElement)) document.activeElement.blur();
  }, [pathname]);

  const handleBlur = (event) => {
    if (event.relatedTarget && containerRef.current?.contains(event.relatedTarget)) return;
    try { if (containerRef.current?.matches(":hover")) return; } catch {}
    scheduleClose();
  };
  const active = Boolean(activeTo);

  return (
    <Box ref={containerRef} sx={{ position: "relative", height: "100%", display: "flex", alignItems: "center" }}>
      <Button
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-active={active ? "true" : "false"}
        onClick={(event) => {
          event.preventDefault();
          openImmediately();
        }}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={openImmediately}
        onBlur={handleBlur}
        startIcon={SectionIcon ? <SectionIcon sx={{ fontSize: 18 }} /> : undefined}
        endIcon={<KeyboardArrowDownIcon aria-label={`${section.label} menu chevron`} sx={{ fontSize: 18, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 180ms ease-out" }} />}
        sx={{
          height: 36, minHeight: 0, alignSelf: "center", borderRadius: "9px", px: 1.5,
          color: active || isOpen ? primaryMain : SB.ink2,
          bgcolor: active ? primaryBg : "transparent",
          fontWeight: 600, textTransform: "none", fontSize: "13.5px",
          "&:hover": { bgcolor: active ? primaryBg : SB.surface3, color: active ? primaryMain : SB.ink },
        }}
      >
        {section.label}
      </Button>
      <Box
        data-testid={`nav-panel-${section.label}`}
        data-open={isOpen ? "true" : "false"}
        aria-hidden={!isOpen}
        onMouseEnter={openImmediately}
        onMouseLeave={scheduleClose}
        onFocus={openImmediately}
        onBlur={handleBlur}
        sx={{
          position: "absolute", top: "calc(100% - 1px)", left: 0, minWidth: 218, py: 0.5,
          bgcolor: SB.surface, border: `1px solid ${SB.line}`, borderRadius: "7px",
          boxShadow: "0 4px 14px rgba(22,18,38,.12)", zIndex: 1200,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateY(0)" : "translateY(-6px)",
          visibility: isVisible ? "visible" : "hidden",
          pointerEvents: isOpen ? "auto" : "none",
          transition: "opacity 180ms ease-out, transform 180ms ease-out",
        }}
      >
        {section.items.map((item) => {
          const Icon = item.icon;
          const itemActive = item.to === activeTo;
          return (
            <Box key={item.to} component={NavLink} to={item.to} tabIndex={isOpen ? 0 : -1} sx={{
              display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25,
              textDecoration: "none", color: itemActive ? primaryMain : SB.ink2,
              fontWeight: itemActive ? 800 : 600, fontSize: "14px",
              bgcolor: itemActive ? primaryBg : "transparent",
              "&:hover": { bgcolor: primaryBg, color: primaryMain },
            }}>
              <Icon sx={{ fontSize: 18, color: itemActive ? primaryMain : SB.ink4 }} />
              <Box sx={{ flex: 1 }}>{item.label}</Box>
              {item.badgeLabel && <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: primaryMain }}>{item.badgeLabel}</Box>}
              {item.badge > 0 && !item.badgeLabel && <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: SB.danger }}>{item.badge}</Box>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function SidebarItem({ to, icon: Icon, label, onClick, badge, badgeLabel, active }) {
  const location = useLocation();
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryBg = alpha(primaryMain, 0.07);
  const toPath = to.split("?")[0];
  const computedActive =
    active == null
      ? toPath === "/"
        ? location.pathname === "/"
        : location.pathname === toPath || location.pathname.startsWith(`${toPath}/`)
      : active;
  const isActive = computedActive;

  return (
    <Box
      component={NavLink}
      to={to}
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: "10px",
        px: "10px", py: "9px", mx: "6px", mb: "2px",
        borderRadius: "10px", textDecoration: "none",
        color: isActive ? primaryMain : SB.ink3,
        bgcolor: isActive ? primaryBg : "transparent",
        fontWeight: isActive ? 700 : 600,
        fontSize: "13.5px",
        transition: "background .12s, color .12s",
        "&:hover": {
          bgcolor: isActive ? primaryBg : SB.surface3,
          color: isActive ? primaryMain : SB.ink,
        },
      }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: "7px", flexShrink: 0,
        display: "grid", placeItems: "center",
        bgcolor: isActive ? primaryBg : "transparent",
        color: isActive ? primaryMain : SB.ink4,
        transition: "all .13s",
      }}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1 }}>{label}</Box>
      {badgeLabel && (
        <Box sx={{
          fontSize: "10.5px", fontWeight: 700, minWidth: 20, height: 18,
          px: "5px", borderRadius: "6px", display: "inline-flex",
          alignItems: "center", justifyContent: "center", flexShrink: 0,
          bgcolor: primaryBg, color: primaryMain,
        }}>
          {badgeLabel}
        </Box>
      )}
      {badge > 0 && !badgeLabel && (
        <Box sx={{
          fontSize: "10.5px", fontWeight: 700, minWidth: 20, height: 18,
          px: "5px", borderRadius: "6px", display: "inline-flex",
          alignItems: "center", justifyContent: "center", flexShrink: 0,
          bgcolor: SB.danger50, color: SB.danger,
        }}>
          {badge}
        </Box>
      )}
    </Box>
  );
}

// ── Sidebar footer item ──────────────────────────────────────────────────────
function SidebarFooterItem({ icon: Icon, label, onClick, danger }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: "10px",
        px: "10px", py: "9px", mx: "6px", mb: "2px",
        borderRadius: "10px", cursor: "pointer",
        color: SB.ink3, fontWeight: 600, fontSize: "13px",
        transition: "background .12s, color .12s",
        "&:hover": {
          bgcolor: danger ? SB.danger50 : SB.surface3,
          color: danger ? SB.danger : SB.ink,
        },
      }}
    >
      <Box sx={{
        width: 28, height: 28, borderRadius: "7px", flexShrink: 0,
        display: "grid", placeItems: "center", color: "inherit",
      }}>
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1 }}>{label}</Box>
    </Box>
  );
}

// ── Global Admin Bar ─────────────────────────────────────────────────────────
function GlobalAdminBar() {
  const { user, activeTenantId, switchTenant } = useTenant();
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryBg = alpha(primaryMain, 0.07);
  const isAdmin = user?.roles?.includes("GLOBAL_ADMIN");
  const [anchorEl, setAnchorEl] = useState(null);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    apiClient.get("/api/v1/admin/tenants").then((res) => {
      if (cancelled) return;
      setTenants(Array.isArray(res.data) ? res.data : res.data?.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const open = Boolean(anchorEl);
  const activeTenant = tenants.find((t) => String(t.id) === String(activeTenantId)) || null;

  return (
    <Box
      data-testid="global-admin-bar"
      sx={{
        // Shown on every screen size: on a phone this bar is the only way for a global admin to pick a training center,
        // so it wraps instead of hiding.
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px",
        px: { xs: "12px", lg: "16px" },
        py: { xs: "6px", lg: 0 },
        minHeight: "40px",
        borderBottom: `1px solid ${SB.line}`,
        bgcolor: primaryMain,
        position: "sticky",
        top: { xs: 0, lg: TOP_NAV_HEIGHT },
        zIndex: 100,
        flexShrink: 0,
      }}
    >
      {/* Left: badge */}
      <Chip
        label="Global Admin"
        size="small"
        sx={{
          height: "22px",
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: ".04em",
          bgcolor: "rgba(255,255,255,.18)",
          color: "#fff",
          border: "1px solid rgba(255,255,255,.25)",
          borderRadius: "6px",
          mr: "8px",
        }}
      />

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Right: tenant switcher */}
      <Box
        component="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex", alignItems: "center", gap: "7px",
          px: "11px", py: "5px", borderRadius: "7px",
          border: "1px solid rgba(255,255,255,.25)",
          background: open ? "rgba(255,255,255,.18)" : "rgba(255,255,255,.1)",
          cursor: "pointer", color: "#fff",
          fontSize: "12.5px", fontWeight: 600,
          "&:hover": { background: "rgba(255,255,255,.18)" },
          transition: "background .12s",
          maxWidth: { xs: "100%", lg: "260px" },
        }}
      >
        <BusinessIcon sx={{ fontSize: 14, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {activeTenant?.name || "Select training center"}
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 15, flexShrink: 0, ml: "auto",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1, minWidth: 240, borderRadius: "10px",
            border: `1px solid ${SB.line}`,
            boxShadow: "0 8px 28px -10px rgba(20,24,40,.20)",
            p: "6px",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => { switchTenant(null); setAnchorEl(null); }}
          selected={!activeTenantId}
          sx={{
            borderRadius: "7px", px: "10px", py: "7px", mb: "2px",
            fontSize: "13px", fontWeight: 600, minHeight: "auto",
            color: !activeTenantId ? primaryMain : SB.ink2,
            bgcolor: !activeTenantId ? primaryBg : "transparent",
            "&:hover": { bgcolor: !activeTenantId ? primaryBg : SB.surface3 },
            "&.Mui-selected": { bgcolor: primaryBg, "&:hover": { bgcolor: primaryBg } },
          }}
        >
          <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Default (All Tenants)
          </Box>
          {!activeTenantId && <CheckIcon sx={{ width: 16, height: 16, color: primaryMain, ml: 1, flexShrink: 0 }} />}
        </MenuItem>
        <Box sx={{ height: 1, bgcolor: SB.line2, mx: "4px", my: "4px" }} />
        {tenants.length === 0 && (
          <MenuItem disabled sx={{ fontSize: "12.5px", color: SB.ink3 }}>No tenants found</MenuItem>
        )}
        {tenants.map((t) => {
          const active = String(t.id) === String(activeTenantId);
          return (
            <MenuItem
              key={t.id}
              selected={active}
              onClick={() => { switchTenant(t.id); setAnchorEl(null); }}
              sx={{
                borderRadius: "7px", px: "10px", py: "7px", mb: "2px",
                fontSize: "13px", fontWeight: 600, minHeight: "auto",
                color: active ? primaryMain : SB.ink2,
                bgcolor: active ? primaryBg : "transparent",
                "&:hover": { bgcolor: active ? primaryBg : SB.surface3 },
                "&.Mui-selected": { bgcolor: primaryBg, "&:hover": { bgcolor: primaryBg } },
              }}
            >
              <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.name || `Tenant ${t.id}`}
              </Box>
              {active && <CheckIcon sx={{ width: 16, height: 16, color: primaryMain, ml: 1, flexShrink: 0 }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}

export default function Layout({ children }) {
  const tenantCtx = useTenant();
  const {
    user,
    effectiveUser,
    logout,
    tenantConfig,
    viewAs,
    setViewAs,
    isFeatureEnabled,
  } = tenantCtx || {};
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryBg = alpha(primaryMain, 0.07);
  const navigate = useNavigate();
  const location = useLocation();
  const isMessagingPage = location.pathname === "/messaging";
  const dispatch = useDispatch();
  const impersonating = useSelector((state) => state.auth.impersonating);
  const [impersonationError, setImpersonationError] = useState(null);
  const [endingImpersonation, setEndingImpersonation] = useState(false);
  const endingImpersonationRef = React.useRef(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [messagingUnread, setMessagingUnread] = React.useState(0);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);

  // Clear badge when user is on the messaging page
  React.useEffect(() => {
    if (isMessagingPage) setMessagingUnread(0);
  }, [isMessagingPage]);

  const handleExitImpersonation = useCallback(async () => {
    if (endingImpersonationRef.current) return;
    setImpersonationError(null);
    if (!impersonating?.sessionId) {
      setImpersonationError("Unable to end impersonation: the session identifier is missing. Sign out and sign in again.");
      return;
    }
    endingImpersonationRef.current = true;
    setEndingImpersonation(true);
    try {
      await endImpersonationAudit({
        sessionId: impersonating.sessionId || null,
        tenantId: impersonating.tenantId || null,
        targetName: impersonating.name || "",
        durationSec: Math.round((Date.now() - (impersonating.startTime || Date.now())) / 1000),
        env: impersonating.env,
      });
    } catch {
      setImpersonationError("Unable to end impersonation. Your session is still active; try again.");
      return;
    } finally {
      endingImpersonationRef.current = false;
      setEndingImpersonation(false);
    }
    clearImpersonationToken();
    setActiveTenant(null);
    dispatch(endImpersonationSession());
    try {
      const res = await apiClient.get("/api/v1/auth/me");
      const data = res.data;
      setCurrentUser({
        ...data,
        tenantId: Object.keys(data.tenantRoles || {})[0] || null,
      });
      dispatch(restoreUser({
        userId: data.id,
        email: data.email,
        username: data.username,
        fullName: data.fullName || null,
        roles: data.roles || [],
        tenantRoles: data.tenantRoles || {},
        agencyId: data.agencyId || null,
        agencyName: data.agencyName || null,
      }));
    } catch {
      setImpersonationError("Impersonation ended, but your admin session could not be restored. Sign in again.");
      return;
    }
    navigate(GLOBAL_ADMIN_PATHS.tenants);
  }, [dispatch, navigate, impersonating]);
  const navUser = effectiveUser || user || { roles: [] };
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const isGlobalAdminUser = userRoles.includes("GLOBAL_ADMIN");
  // The dev clock route only exists under the backend's local profile, so it answering is what "running locally" means.
  const [devClockAvailable, setDevClockAvailable] = React.useState(false);
  useEffect(() => {
    if (!isGlobalAdminUser) return;
    let cancelled = false;
    getDevClock().then(() => { if (!cancelled) setDevClockAvailable(true); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isGlobalAdminUser]);

  const showClassPages = isFeatureEnabled
    ? isFeatureEnabled("classes")
    : !!tenantConfig?.features?.classes;
  const showAgencyManagement = isFeatureEnabled
    ? isFeatureEnabled("agency")
    : !!tenantConfig?.features?.agency;
  const showCertManagement = isFeatureEnabled
    ? isFeatureEnabled("certManagement")
    : true;
  const showEquipmentManagement = isFeatureEnabled
    ? isFeatureEnabled("equipmentManagement")
    : !!tenantConfig?.features?.equipmentManagement;
  const showSemesterPlanning = isFeatureEnabled
    ? isFeatureEnabled("semesterPlanning")
    : !!tenantConfig?.features?.semesterPlanning;
  // Determine available views based on user roles
  const canAccessInstructorView = userRoles.includes("INSTRUCTOR");
  const canAccessCoordinatorView = userRoles.includes("COURSE_COORDINATOR");
  const canAccessAdminView = userRoles.includes("ADMIN") || userRoles.includes("GLOBAL_ADMIN");
  const canAccessStudentView =
    userRoles.includes("STUDENT") ||
    userRoles.includes("MEMBER") ||
    userRoles.includes("INSTRUCTOR");

  // Build available views in order
  const availableViews = [];
  if (canAccessInstructorView) availableViews.push("INSTRUCTOR");
  if (canAccessCoordinatorView) availableViews.push("COURSE_COORDINATOR");
  if (canAccessAdminView) availableViews.push("ADMIN");
  if (canAccessStudentView) availableViews.push("STUDENT");

  // Determine default view if viewAs is not set
  let defaultView = availableViews[0] || null;
  const effectiveView = viewAs || defaultView;

  const showStudentPages = effectiveView === "STUDENT";
  const showInstructorPages = effectiveView === "INSTRUCTOR";
  const showCoordinatorPages = effectiveView === "COURSE_COORDINATOR";
  const showAdminPages = effectiveView === "ADMIN";
  const showCoordinatorOrAdminPages = showCoordinatorPages || showAdminPages;

  const { cartItems, removeFromCart, clearCart, isCartOpen, setIsCartOpen } =
    useCart();

  const currentUser = navUser;
  const userInitials = (
    currentUser?.fullName ||
    currentUser?.name ||
    currentUser?.email ||
    "U"
  )
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Favicon
  React.useEffect(() => {
    if (tenantConfig?.icon) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = tenantConfig.icon;
    }
  }, [tenantConfig]);

  // Poll for total unread message count every 60 seconds
  useEffect(() => {
    if (!user || isGlobalAdminUser) return;
    let cancelled = false;
    const fetchUnread = () => {
      fetchMessagingThreads().then((data) => {
        if (cancelled) return;
        const threads = data?.threads || {};
        const total = Object.values(threads).reduce((sum, t) => sum + (t.unread || 0), 0);
        setMessagingUnread(total);
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, isGlobalAdminUser]);

  const getNavSections = () => {
    if (!navUser) return [];

    const sections = [];

    // ── Overview ──
    const overviewItems = [
      { to: "/", label: "Dashboard", icon: GridViewIcon },
    ];
    sections.push({ label: "Overview", items: overviewItems });

    // ── Instructor Teaching ──
    if (showClassPages && showInstructorPages && navUser.roles?.includes("INSTRUCTOR")) {
      const teachingItems = [];
      teachingItems.push({ to: "/instructor/courses", label: "Assigned Classes", icon: SchoolIcon });
      teachingItems.push({ to: "/instructor/scheduling", label: "Scheduling", icon: CalendarMonthIcon });
      sections.push({ label: "Teaching", items: teachingItems });

      const proposalItems = [];
      proposalItems.push({ to: "/instructor/proposals", label: "My Proposals", icon: CalendarMonthIcon });
      proposalItems.push({ to: "/courses/submit", label: "Submit Course", icon: AddIcon });
      sections.push({ label: "Proposals", icon: DescriptionIcon, items: proposalItems });

      if (showCertManagement) {
        sections.push({ label: "Credentials", items: [{ to: "/certifications", label: "Certifications", icon: WorkspacePremiumIcon }] });
      }
    }

    // ── Student Learning ──
    if (showStudentPages) {
      const learningItems = [];
      if (showClassPages) {
        learningItems.push({ to: "/transcript", label: "My Classes", icon: SchoolIcon });
        learningItems.push({ to: "/classes", label: "Course Catalog", icon: MenuBookIcon });
      }
      const canAccessCertifications =
        showCertManagement &&
        (userRoles.includes("STUDENT") ||
          userRoles.includes("MEMBER") ||
          userRoles.includes("INSTRUCTOR") ||
          userRoles.includes("COURSE_COORDINATOR") ||
          userRoles.includes("ADMIN") ||
          userRoles.includes("GLOBAL_ADMIN"));
      if (canAccessCertifications) {
        learningItems.push({ to: "/certifications", label: "Certifications", icon: WorkspacePremiumIcon });
      }
      if (learningItems.length > 0) {
        sections.push({ label: "Learning", items: learningItems });
      }
    }

    // ── Coordinator / Admin Management ──
    if (showClassPages && showCoordinatorOrAdminPages) {
      const mgmtItems = [];
      mgmtItems.push({ to: "/course-management", label: "Course Management", icon: ManageAccountsIcon });
    
      if (showClassPages && showCoordinatorOrAdminPages) {
        mgmtItems.push({ to: "/instructor/knowledge-bank", label: "Knowledge Bank", icon: LibraryBooksIcon });
      }

      mgmtItems.push({ to: "/instructor-management", label: "Instructor Management", icon: ManageAccountsIcon });
      mgmtItems.push({ to: "/rooms", label: "Room Management", icon: MeetingRoomIcon });
      mgmtItems.push({ to: "/coordinator/approvals", label: "Course Approvals", icon: AssignmentTurnedInIcon });
      if (showSemesterPlanning) {
        mgmtItems.push({ to: "/semesters", label: "Semester Planning", icon: CalendarMonthIcon });
      }
      if (showAdminPages && showCertManagement) {
        mgmtItems.push({ to: "/certifications?tab=ADMIN", label: "Certification Types", icon: WorkspacePremiumIcon });
      }
      // Admins reach settings from the gear icon; coordinators may use the page too but have no gear.
      if (!showAdminPages) {
        mgmtItems.push({ to: "/training-center", label: "Training Center Settings", icon: SettingsIcon });
      }
      if (mgmtItems.length > 0) {
        sections.push({ label: "Training Center", items: mgmtItems });
      }
    }

    // ── Global Admin (always visible for GLOBAL_ADMIN, independent of tenant feature flags) ──
    if (userRoles.includes("GLOBAL_ADMIN")) {
      const globalAdminItems = [
        { to: "/tenant-setup", label: "Tenant Setup", icon: AdminPanelSettingsIcon },
        { to: "/admin/user-roles", label: "User Roles", icon: ManageAccountsIcon },
        { to: "/standard-courses", label: "Standardize Courses", icon: LibraryBooksIcon },
        { to: GLOBAL_ADMIN_PATHS.auditLog, label: "Audit Log", icon: HistoryIcon },
        { to: GLOBAL_ADMIN_PATHS.systemHealth, label: "System Health", icon: MonitorHeartIcon },
        ...(devClockAvailable ? [{ to: GLOBAL_ADMIN_PATHS.timeTravel, label: "Time Travel", icon: ScheduleIcon }] : []),
      ];
      sections.push({ label: "Global Admin", items: globalAdminItems });
    } else if (userRoles.includes("ADMIN")) {
      // ADMIN is tenant-scoped: user-role management for their own tenant, but
      // not cross-tenant Tenant Setup.
      sections.push({
        label: "Admin",
        items: [{ to: "/admin/user-roles", label: "User Roles", icon: ManageAccountsIcon }],
      });
    }

    // ── Agency (only when the tenant's agency feature is enabled) ──
    if (showAgencyManagement) {
      if (
        userRoles.includes("AGENCY_ADMIN") ||
        userRoles.includes("GLOBAL_ADMIN") ||
        userRoles.includes("ADMIN")
      ) {
        const agencyItems = [];
        agencyItems.push({ to: "/agency/member-compliance", label: "Member Compliance", icon: CheckCircleIcon });
        agencyItems.push({ to: "/agency/class-enrollments", label: "Class Enrollments", icon: SchoolIcon });
        agencyItems.push({ to: "/agency/role-requirements", label: "Role Requirements", icon: AssignmentIcon });
        agencyItems.push({ to: "/agency/coi", label: "Insurance (COI)", icon: DescriptionIcon });
        agencyItems.push({ to: "/agency", label: "Agency Management", icon: BusinessIcon });

        sections.push({ label: "Agency", items: agencyItems });
      } else if (userRoles.includes("COURSE_COORDINATOR")) {
        sections.push({ label: "Agency", items: [{ to: "/agency/coi", label: "Insurance (COI)", icon: DescriptionIcon }] });
      }
    }

    // ── Equipment ──
    if (
      showEquipmentManagement &&
      (navUser.roles?.includes("AGENCY_ADMIN") ||
        navUser.roles?.includes("GLOBAL_ADMIN") ||
        navUser.roles?.includes("ADMIN"))
    ) {
      sections.push({
        label: "Equipment",
        items: [{ to: "/equipment", label: "Equipment (AEDs)", icon: MedicalServicesIcon }],
      });
    }

    return sections;
  };

  const navSections = getNavSections();

  // Public / unauthenticated
  if (!user) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {children}
      </Box>
    );
  }

  // ── Sidebar content (shared between permanent + mobile drawer) ────────────
  const sidebarContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: SB.surface,
        borderRight: `1px solid ${SB.line}`,
      }}
    >
      {/* ── Header: org identity ── */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: "10px",
          px: "18px", py: "18px 18px 16px",
          borderBottom: `1px solid ${SB.line2}`,
          cursor: "pointer",
        }}
        onClick={() => navigate("/")}
      >
        <TenantLogo src={tenantConfig?.logo} name={tenantConfig?.name} size={32} color={primaryMain} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: "13px", fontWeight: 600, color: SB.ink,
            lineHeight: 1.2, fontFamily: "var(--font-display)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {tenantConfig?.name || "Training Center"}
          </Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 500, color: SB.ink4, mt: "1px" }}>
            Training Center
          </Typography>
        </Box>
      </Box>

      {/* ── User identity ── */}
      <Box
        sx={{
          display: "flex", alignItems: "center", gap: "11px",
          px: "18px", py: "14px",
          bgcolor: SB.surface2,
          borderBottom: `1px solid ${SB.line2}`,
        }}
      >
        <Avatar
          sx={{
            width: 38, height: 38, fontSize: "13px", fontWeight: 800,
            bgcolor: primaryMain, color: "#fff", flexShrink: 0,
            border: `2px solid ${alpha(primaryMain, 0.15)}`,
          }}
        >
          {userInitials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: SB.ink }}>
            {currentUser?.fullName || currentUser?.name || currentUser?.email}
          </Typography>
          <Typography sx={{
            fontSize: "11px", fontWeight: 600, color: SB.ink3, mt: "2px",
            letterSpacing: "-.01em",
          }}>
            {effectiveView === "INSTRUCTOR" ? "Instructor" : effectiveView === "STUDENT" ? "Student" : effectiveView === "COURSE_COORDINATOR" ? "Coordinator" : effectiveView === "ADMIN" ? "Admin" : "Member"}
          </Typography>
        </Box>
      </Box>

      {/* ── Role switcher ── */}
      {availableViews.length > 1 && (
        <Box sx={{
          display: "flex", gap: 0, mx: "16px", mt: "14px", mb: "4px",
          bgcolor: SB.surface3, border: `1.5px solid ${SB.line}`,
          borderRadius: "11px", p: "3px",
        }}>
          {availableViews.map((view) => {
            const isActive = effectiveView === view;
            const label =
              view === "INSTRUCTOR" ? "Instructor" :
              view === "COURSE_COORDINATOR" ? "Coordinator" :
              view === "ADMIN" ? "Admin" :
              view === "STUDENT" ? "Student" : view;
            return (
              <Box
                key={view}
                onClick={() => setViewAs && setViewAs(view === defaultView ? null : view)}
                sx={{
                  flex: 1, height: 30, borderRadius: "8px",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  gap: "6px", cursor: "pointer", fontSize: "12.5px", fontWeight: 700,
                  color: isActive ? "#fff" : SB.ink3,
                  bgcolor: isActive ? primaryMain : "transparent",
                  boxShadow: isActive ? `0 2px 8px -2px ${alpha(primaryMain, 0.4)}` : "none",
                  transition: "all .15s",
                  "&:hover": { color: isActive ? "#fff" : SB.ink },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Global Admin Switcher ── */}
      {false && (navUser.roles?.includes("GLOBAL_ADMIN") || navUser.roles?.includes("ADMIN")) && (
        <Box sx={{ px: "4px", pb: "6px" }}>
          <SidebarSectionLabel label="Global Admin" />
          <GlobalAdminSwitcher />
        </Box>
      )}

      {/* ── Navigation sections ── */}
      <Box sx={{ flex: 1, minHeight: 0, py: "10px", px: "4px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 0 }}>
        {navSections.map((section) => {
          const activeTo = bestMatch(location.pathname, section.items);
          return (
            <React.Fragment key={section.label}>
              <SidebarSectionLabel label={section.label} />
              {section.items.map((item) => (
                <SidebarItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => setMobileOpen(false)}
                  badge={item.badge}
                  badgeLabel={item.badgeLabel}
                  active={item.to === activeTo}
                />
              ))}
            </React.Fragment>
          );
        })}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        borderTop: `1px solid ${SB.line2}`, p: "10px",
        display: "flex", flexDirection: "column", gap: "2px",
      }}>
        <SidebarFooterItem
          icon={PersonIcon}
          label="Profile"
          onClick={() => { setMobileOpen(false); navigate("/profile"); }}
        />
        {isGlobalAdminUser && (
          <SidebarFooterItem
            icon={SwapHorizIcon}
            label="Tenant switcher"
            onClick={() => { setMobileOpen(false); navigate(GLOBAL_ADMIN_PATHS.tenants); }}
          />
        )}
        <SidebarFooterItem
          icon={LogoutIcon}
          label="Sign Out"
          onClick={logout}
          danger
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        data-testid="desktop-top-navigation"
        component="header"
        sx={{
          display: { xs: "none", lg: "block" },
          position: "fixed", top: 0, left: 0, right: 0, height: TOP_NAV_HEIGHT,
          bgcolor: "rgba(255,255,255,.92)", backdropFilter: "blur(14px)",
          borderBottom: `1px solid ${SB.line}`, boxShadow: "none", zIndex: 1100,
        }}
      >
        <Box
          data-testid="desktop-top-navigation-content"
          sx={{
            position: "relative", display: "flex", alignItems: "center", gap: 2,
            width: "100%", maxWidth: "1380px", height: "100%", mx: "auto", px: "40px",
            boxSizing: "border-box",
          }}
        >
        <Box
          data-testid="desktop-top-navigation-primary-group"
          sx={{ display: "flex", alignItems: "center", alignSelf: "stretch", gap: 4, minWidth: 0 }}
        >
          <Box component={NavLink} to="/" sx={{ display: "flex", alignItems: "center", gap: 1, color: "inherit", textDecoration: "none", minWidth: 0 }}>
            <TenantLogo src={tenantConfig?.logo} name={tenantConfig?.name} size={34} color={primaryMain} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: SB.ink, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "var(--font-display)" }}>
                {tenantConfig?.name || "Training Center"}
              </Typography>
              <Typography sx={{ fontSize: "10.5px", color: SB.ink4, lineHeight: 1.2 }}>Training Center</Typography>
            </Box>
          </Box>

          <Box component="nav" aria-label="Primary navigation" sx={{
            position: "static", transform: "none",
            display: "flex", alignItems: "stretch", alignSelf: "stretch", gap: 0.25,
            minWidth: 0, height: "100%", whiteSpace: "nowrap",
          }}>
          {navSections.map((section) => {
            const activeTo = bestMatch(location.pathname, section.items);
            if (section.items.length > 1) {
              return <TopNavMenu key={section.label} section={section} activeTo={activeTo} primaryMain={primaryMain} primaryBg={primaryBg} icon={section.icon || section.items[0]?.icon} />;
            }
            const item = section.items[0];
            const active = item.to === activeTo;
            const ItemIcon = item.icon;
            return (
              <Box key={section.label} component={NavLink} to={item.to} data-testid={`top-nav-direct-${section.label}`} sx={{
                display: "flex", alignItems: "center", gap: 0.75, alignSelf: "center",
                height: 36, px: 1.5, borderRadius: "9px",
                color: active ? primaryMain : SB.ink2,
                bgcolor: active ? primaryBg : "transparent",
                textDecoration: "none", fontSize: "13.5px", fontWeight: 600,
                "&:hover": { bgcolor: active ? primaryBg : SB.surface3, color: active ? primaryMain : SB.ink },
              }}>
                {ItemIcon && <ItemIcon sx={{ fontSize: 18 }} />}
                {item.label}
                {item.badgeLabel && <Box sx={{ fontSize: "10.5px", fontWeight: 700 }}>{item.badgeLabel}</Box>}
                {item.badge > 0 && !item.badgeLabel && <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: SB.danger }}>{item.badge}</Box>}
              </Box>
            );
          })}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", minWidth: 0 }}>
        {!isGlobalAdminUser && (
          <Box
            component={NavLink}
            to="/messaging"
            sx={{
              display: "inline-flex", alignItems: "center", gap: 0.75, px: 0.75,
              color: SB.ink2, textDecoration: "none", fontSize: "14px", fontWeight: 650,
              "&:hover": { color: SB.ink },
            }}
          >
            <Badge badgeContent={messagingUnread} color="error" max={99} invisible={messagingUnread === 0}>
              <ForumIcon sx={{ fontSize: 20 }} />
            </Badge>
            Messages
          </Box>
        )}

        {showAdminPages && (
          <IconButton
            component={NavLink}
            to="/training-center"
            aria-label="Training Center Settings"
            title="Training Center Settings"
            size="small"
            sx={{ color: SB.ink2, "&:hover": { color: SB.ink, bgcolor: SB.surface2 } }}
          >
            <SettingsIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        <Button aria-label="Open profile menu" onClick={(event) => setProfileAnchorEl(event.currentTarget)} sx={{ textTransform: "none", color: SB.ink, gap: 1, minWidth: 0 }}>
          <Avatar sx={{ width: 32, height: 32, fontSize: "12px", fontWeight: 700, bgcolor: primaryMain, border: `2.5px solid ${primaryBg}`, fontFamily: "var(--font-display)" }}>{userInitials}</Avatar>
          <Typography sx={{ display: { md: "block", lg: "block" }, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px", fontWeight: 700 }}>
            {currentUser?.fullName || currentUser?.name || currentUser?.email}
          </Typography>
        </Button>
        <Menu anchorEl={profileAnchorEl} open={Boolean(profileAnchorEl)} onClose={() => setProfileAnchorEl(null)}>
          <MenuItem onClick={() => { setProfileAnchorEl(null); navigate("/profile"); }}><PersonIcon sx={{ mr: 1, fontSize: 18 }} />Profile</MenuItem>
          {isGlobalAdminUser && (
            <MenuItem onClick={() => { setProfileAnchorEl(null); navigate(GLOBAL_ADMIN_PATHS.tenants); }}><SwapHorizIcon sx={{ mr: 1, fontSize: 18 }} />Tenant switcher</MenuItem>
          )}
          {availableViews.length > 1 && <Divider component="li" />}
          {availableViews.length > 1 && (
            <Typography component="li" role="presentation" sx={{ px: 2, pt: 0.5, pb: 0.25, fontSize: "10.5px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: SB.ink4, listStyle: "none" }}>
              Switch view
            </Typography>
          )}
          {availableViews.length > 1 && availableViews.map((view) => (
            <MenuItem
              key={view}
              selected={effectiveView === view}
              aria-label={`Switch to ${VIEW_LABELS[view] || view} view`}
              onClick={() => {
                setProfileAnchorEl(null);
                setViewAs && setViewAs(view === defaultView ? null : view);
              }}
            >
              <Box component="span" sx={{ flex: 1 }}>{VIEW_LABELS[view] || view}</Box>
              {effectiveView === view && <CheckIcon sx={{ width: 16, height: 16, color: primaryMain, ml: 2 }} />}
            </MenuItem>
          ))}
          {availableViews.length > 1 && <Divider component="li" />}
          <MenuItem onClick={() => { setProfileAnchorEl(null); logout(); }}><LogoutIcon sx={{ mr: 1, fontSize: 18 }} />Sign Out</MenuItem>
        </Menu>
        </Box>
        </Box>
      </Box>

      {/* ── Mobile drawer ────────────────────────────────────────────────── */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <Box
        component="main"
        data-testid="layout-main"
        data-desktop-padding-top={`${TOP_NAV_HEIGHT}px`}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
          pt: { xs: 0, lg: `${TOP_NAV_HEIGHT}px` },
        }}
      >
        {/* Mobile top bar (hamburger only) */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            bgcolor: SB.bg,
          }}
        >
          <IconButton onClick={() => setMobileOpen(true)} size="small">
            <MenuIcon />
          </IconButton>
          <Typography
            fontWeight={600}
            sx={{ color: primaryMain, ml: 1.5, fontSize: "0.95rem", fontFamily: "var(--font-display)" }}
          >
            {tenantConfig?.name || "Training Center"}
          </Typography>
        </Box>

        {/* ── Global Admin persistent bar ─────────────────────────────────── */}
        <GlobalAdminBar />

        {/* ── Registration Cart Drawer ────────────────────────────────────── */}
        <Drawer
          anchor="right"
          open={isCartOpen}
          onClose={() => setIsCartOpen(false)}
        >
          <Box
            sx={{
              width: 350,
              p: 3,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary.main"
              mb={1}
            >
              Registration Cart
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
              {cartItems.length === 0 ? (
                <Typography color="text.secondary" align="center" mt={4}>
                  Your cart is empty.
                </Typography>
              ) : (
                <List disablePadding>
                  {cartItems.map((item) => (
                    <ListItem
                      key={item.id}
                      sx={{
                        bgcolor: "var(--color-surface-2)",
                        mb: 1,
                        borderRadius: "8px",
                        border: "1px solid var(--color-hairline-cloud)",
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography fontWeight={600} variant="body2">
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            color="primary.main"
                            fontWeight={700}
                          >
                            $450.00
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {cartItems.length > 0 && (
              <Box sx={{ mt: 3, borderTop: "1px solid var(--color-hairline-cloud)", pt: 2 }}>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    Course Selection
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {cartItems.length} Selected
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" mb={3}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    Estimated Fee
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                  >
                    ${(cartItems.length * 450).toFixed(2)}
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={async () => {
                    try {
                      await Promise.all(
                        cartItems.map((c) => registerForClass(c.id, user.id)),
                      );
                      clearCart();
                      setIsCartOpen(false);
                      alert("Successfully registered for classes!");
                    } catch (err) {
                      alert("Error registering for some classes.");
                    }
                  }}
                  sx={{
                    bgcolor: "#0a3161",
                    "&:hover": { bgcolor: "#062244" },
                    fontWeight: 700,
                    borderRadius: "8px",
                  }}
                >
                  Checkout
                </Button>
              </Box>
            )}
          </Box>
        </Drawer>

        {/* Page content */}
        <Box sx={isMessagingPage
          ? { flex: "1 1 0", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }
          : { flexGrow: 1, p: 3 }
        }>
          {children}
        </Box>

        {/* Footer */}
        {!isMessagingPage && (
          <Box
            component="footer"
            sx={{
              px: 3,
              py: 1.5,
              borderTop: "1px solid rgba(0,0,0,0.08)",
              bgcolor: SB.bg,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              © 2026 Atomic Solutions LLC. All rights reserved.
            </Typography>
          </Box>
        )}
      </Box>
      {impersonationError && (
        <Alert severity="error" sx={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", zIndex: 10000, maxWidth: "calc(100vw - 40px)", width: "max-content" }}>
          {impersonationError}
        </Alert>
      )}
      {impersonating && (
        <Box
          role="status"
          aria-live="assertive"
          sx={{
            position: "fixed",
            bottom: "24px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            gap: "13px",
            background: "#0f172a",
            color: "#fff",
            borderRadius: "999px",
            padding: "8px 8px 8px 17px",
            boxShadow: "0 14px 36px -10px rgba(0,0,0,.5)",
            maxWidth: "calc(100vw - 40px)",
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80", flexShrink: 0, animation: "pulse 2s infinite" }} />
          <Typography sx={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Impersonating <Box component="strong" sx={{ fontWeight: 700 }}>{impersonating.name}</Box> ({impersonating.roleLabel}) · {impersonating.tenantName}
          </Typography>
          <Button
            onClick={handleExitImpersonation}
            disabled={endingImpersonation}
            sx={{ border: 0, cursor: "pointer", background: "#fff", color: "#0f172a", fontSize: "12.5px", fontWeight: 700, padding: "7px 15px", borderRadius: "999px", textTransform: "none", flexShrink: 0, minWidth: 0, lineHeight: 1, "&:hover": { opacity: 0.85 } }}
          >
            Exit
          </Button>
        </Box>
      )}
    </Box>
  );
}
